export interface ColorMap {
  [key: string]: { mediumColor: string; lightColor: string; darkColor: string };
}

// USAGE: colorMap[selectedImprovePlan?.question?.color]?.theme)
export const colorMap: ColorMap = {
  sunYellow: { mediumColor: '#FCD300', lightColor: '#ffe665', darkColor: '#d4b202' },
  aquaGreen: { mediumColor: '#06BF97', lightColor: '#3be1ce', darkColor: '#069b7b' },
  calmLilac: { mediumColor: '#BEAAF3', lightColor: '#d4c6f9', darkColor: '#6048a1' },
  warmOrange: { mediumColor: '#FF8A4A', lightColor: '#ffaf83', darkColor: '#d96525' },
  zingyGreen: { mediumColor: '#8EEA36', lightColor: '#bbf484', darkColor: '#4c8f0c' },
  skyBlue: { mediumColor: '#019DF2', lightColor: '#3fb9fc', darkColor: '#0072b0' },
  darkGrey: { mediumColor: '#505050', lightColor: '#667080', darkColor: '#505050' },
};

export const defaultColors = {
  mediumColor: '#f4f2ec',
  lightColor: '#fcfbf7',
  darkColor: '#ece7dc',
};

export type ColorName =
  | 'sunYellow'
  | 'aquaGreen'
  | 'calmLilac'
  | 'warmOrange'
  | 'zingyGreen'
  | 'skyBlue'
  | 'darkGrey';

export const getColorHex = (
  colorName: string | null | undefined,
  shade: 'mediumColor' | 'lightColor' | 'darkColor' = 'mediumColor',
): string => {
  // Default to darkGrey if colorName is null, undefined, or not found
  const defaultColor = colorMap.darkGrey[shade];

  if (!colorName) {
    return defaultColor;
  }

  // Find color with case-insensitive matching
  const normalizedColorName = findColorKey(colorName);
  if (!normalizedColorName) {
    return defaultColor;
  }

  return colorMap[normalizedColorName][shade];
};

export const getColorClassName = (colorName: string | null | undefined): string => {
  if (!colorName) {
    return 'darkGrey'; // default class
  }

  if (colorName) {
    if (
      colorName === 'sunYellow' ||
      colorName === 'aquaGreen' ||
      colorName === 'calmLilac' ||
      colorName === 'warmOrange' ||
      colorName === 'zingyGreen' ||
      colorName === 'skyBlue' ||
      colorName === 'darkGrey'
    ) {
      const normalizedColorName = findColorKey(colorName);
      if (!normalizedColorName) {
        return 'darkGrey'; // default class
      }
    } else {
      const matchingTheme =
        Object.entries(colorMap).find(
          ([_, value]) => value.mediumColor.toLowerCase() === colorName.toLowerCase(),
        )?.[0] || 'darkGrey';

      return matchingTheme || 'darkGrey';
    }
  }

  return 'darkGrey';
};

const findColorKey = (colorName: string): string | null => {
  const lowerColorName = colorName.toLowerCase().trim();

  // Find matching key (case-insensitive)
  const matchingKey = Object.keys(colorMap).find((key) => key.toLowerCase() === lowerColorName);

  return matchingKey || null;
};
